import {
  PerformanceCurvePoint,
  PerformanceDadoDiario,
  PerformancePontoCambio,
  PerformancePontoDerivativo,
  PerformancePontoJuros,
  PerformanceStreamCurve,
} from '@services/http/performance/performance-http.service';
import { PerformanceCurveSeries } from '@interfaces/dashboard/responses/dashboard-economy-scenario.response';

function getDateKey(value: string | null | undefined): string {
  if (!value) return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

function hasMeaningfulValue(value: number | null | undefined): boolean {
  return Number.isFinite(value) && Math.abs(value ?? 0) > 0.000001;
}

function resolveFxQuote(
  ptaxValue: number | null | undefined,
  accumulatedValue: number | null | undefined,
  initialQuote: number
): number | null {
  const ptax = Number(ptaxValue);
  if (Number.isFinite(ptax) && ptax > 0) return ptax;

  const accumulated = Number(accumulatedValue);
  if (!Number.isFinite(accumulated) || accumulated <= 0) return null;

  // Alguns payloads chamam a cotação absoluta de "variacaoAcumulada".
  // Multiplicador acumulado fica perto de 1; cotação BRL vem na faixa de 4-7.
  if (accumulated > 2) return accumulated;
  if (initialQuote > 0) return initialQuote * accumulated;

  return null;
}

function resolveFxAccumulatedReturn(
  ptaxValue: number | null | undefined,
  accumulatedValue: number | null | undefined,
  initialQuote: number
): number | null {
  const quote = resolveFxQuote(ptaxValue, accumulatedValue, initialQuote);
  if (quote !== null && initialQuote > 0) return (quote / initialQuote - 1) * 100;

  const accumulated = Number(accumulatedValue);
  if (Number.isFinite(accumulated) && accumulated > 0 && accumulated <= 2) {
    return (accumulated - 1) * 100;
  }

  return null;
}

function findFirstDebtActivityIndex(
  totalSeries: PerformanceCurvePoint[],
  jurosSeries: PerformancePontoJuros[] = [],
  cambioSeries: PerformancePontoCambio[] = [],
  derivCurve: PerformancePontoDerivativo[] = []
): number {
  const jurosByDate = new Map(jurosSeries.map(p => [getDateKey(p.data), p]));
  const cambioByDate = new Map(cambioSeries.map(p => [getDateKey(p.data), p]));
  const derivByDate = new Map(derivCurve.map(p => [getDateKey(p.data), p]));

  const index = totalSeries.findIndex(point => {
    const date = getDateKey(point.data);
    const juros = jurosByDate.get(date);
    const cambio = cambioByDate.get(date);
    const deriv = derivByDate.get(date);

    return (
      hasMeaningfulValue(point.cotaTotal) ||
      hasMeaningfulValue(juros?.cotaJurosAcumulada) ||
      hasMeaningfulValue(cambio?.varacacaoAcumulada ?? cambio?.variacaoAcumulada) ||
      hasMeaningfulValue(deriv?.cotaAcumulada) ||
      hasMeaningfulValue(deriv?.mtmNaCurva)
    );
  });

  return index >= 0 ? index : 0;
}

/**
 * Parâmetros opcionais do benchmark para o dataset CDI.
 * Quando não fornecido, a série CDI é omitida.
 */
export interface BenchmarkMeta {
  nome: string;
  cor: string;
  tipo?: 'CDI' | 'PRE';
  taxaAnual?: number;
}

function buildPreBenchmarkData(labels: string[], annualRate: number): number[] {
  const normalizedRate = Number.isFinite(annualRate) ? annualRate / 100 : 0;
  return labels.map((_label, index) => {
    if (index === 0) return 0;
    return (Math.pow(1 + normalizedRate, index / 252) - 1) * 100;
  });
}

/**
 * Constrói o { labels, datasets } para o PerformanceMainChartComponent
 * a partir de um PerformanceStreamCurve (resultado do SSE).
 *
 * Espelha fielmente o buildMainChartData de loan-performance.component.ts.
 */
export function buildChartDataFromStream(
  curve: PerformanceStreamCurve,
  benchmark: BenchmarkMeta,
  chartOptions?: Record<string, unknown>,
  benchmarkLabel?: string
): { labels: string[]; datasets: unknown[] } | null {
  const rawTotalSeries = curve?.series?.total?.curve;
  if (!Array.isArray(rawTotalSeries) || rawTotalSeries.length === 0) return null;

  const rawJurosSeries: PerformancePontoJuros[] = curve?.series?.juros ?? [];
  const rawCambioSeries: PerformancePontoCambio[] = curve?.series?.cambio ?? [];
  const rawDerivCurve: PerformancePontoDerivativo[] = curve?.series?.derivativos?.curve ?? [];
  const firstActivityIndex = findFirstDebtActivityIndex(
    rawTotalSeries,
    rawJurosSeries,
    rawCambioSeries,
    rawDerivCurve
  );
  // Mantém um ponto âncora zerado antes da primeira atividade: a Cota Total inicia em 0.
  const startIndex = Math.max(0, firstActivityIndex - 1);
  const startDate = getDateKey(rawTotalSeries[startIndex]?.data);
  const totalSeries = rawTotalSeries.slice(startIndex);
  const labels = totalSeries.map((p: PerformanceCurvePoint) => p.data);
  const dadosDiarios: PerformanceDadoDiario[] = (curve?.dadosDiarios ?? []).filter(
    d => getDateKey(d.data) >= startDate
  );

  const dailyByDate = new Map<string, PerformanceDadoDiario>();
  for (const d of dadosDiarios) {
    dailyByDate.set(getDateKey(d.data), d);
  }

  const datasets: unknown[] = [];

  // 1. Cota Total (Curva) — verde, com preenchimento: abaixo do benchmark é o resultado bom (mesma leitura do Relatório de Posição)
  datasets.push({
    label: 'Cota Total (Curva)',
    data: totalSeries.map((p: PerformanceCurvePoint) => (p.cotaTotal ?? 0) * 100),
    borderColor: '#48bb78',
    backgroundColor: 'rgba(72, 187, 120, 0.1)',
    borderWidth: 3,
    tension: 0.35,
    fill: true,
    yAxisID: 'y',
  });

  // 2. Juros — dark gray solid (hidden by default)
  const jurosSeries: PerformancePontoJuros[] = rawJurosSeries.filter(
    p => getDateKey(p.data) >= startDate
  );
  if (jurosSeries.length > 0) {
    datasets.push({
      label: 'Juros',
      data: jurosSeries.map((p: PerformancePontoJuros) => (p.cotaJurosAcumulada ?? 0) * 100),
      borderColor: '#2d3748',
      backgroundColor: 'rgba(45, 55, 72, 0.15)',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
      hidden: true,
    });
  }

  // 3. Câmbio Ponderado — blue dashed (hidden by default)
  const cambioSeries: PerformancePontoCambio[] = rawCambioSeries.filter(
    p => getDateKey(p.data) >= startDate
  );
  if (cambioSeries.length > 0) {
    datasets.push({
      label: 'Câmbio Ponderado',
      data: cambioSeries.map((p: PerformancePontoCambio) => (p.varacacaoAcumulada ?? 0) * 100),
      borderColor: '#3182ce',
      backgroundColor: 'rgba(49, 130, 206, 0.12)',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      borderDash: [8, 4],
      yAxisID: 'y',
      hidden: true,
    });
  }

  // 4. Derivativos (Curva) — purple solid (hidden by default)
  const derivCurve: PerformancePontoDerivativo[] = rawDerivCurve.filter(
    p => getDateKey(p.data) >= startDate
  );
  if (
    derivCurve.length > 0 &&
    derivCurve.some(
      (p: PerformancePontoDerivativo) => (p.cotaAcumulada ?? 0) !== 0 || (p.mtmNaCurva ?? 0) !== 0
    )
  ) {
    datasets.push({
      label: 'Derivativos (Curva)',
      data: derivCurve.map((p: PerformancePontoDerivativo) => (p.cotaAcumulada ?? 0) * 100),
      borderColor: '#9f7aea',
      backgroundColor: 'rgba(159, 122, 234, 0.15)',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
      hidden: true,
    });
  }

  const taxaUsdInicial = curve?.taxaUsdInicial ?? 0;
  const taxaEurInicial = curve?.taxaEurInicial ?? 0;

  // 5. USD Acumulado — dark blue dashed (hidden by default)
  const hasUsd = dadosDiarios.some(
    (d: PerformanceDadoDiario) =>
      resolveFxAccumulatedReturn(d.ptaxUsd, d.variacaoAcumuladaUsd, taxaUsdInicial) !== null
  );
  if (hasUsd) {
    datasets.push({
      label: 'USD Acumulado',
      data: totalSeries.map((p: PerformanceCurvePoint) => {
        const d = dailyByDate.get(getDateKey(p.data));
        return resolveFxAccumulatedReturn(d?.ptaxUsd, d?.variacaoAcumuladaUsd, taxaUsdInicial);
      }),
      borderColor: '#2b6cb0',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      borderDash: [6, 6],
      fill: false,
      yAxisID: 'y',
      hidden: true,
    });
  }

  // 6. EUR Acumulado — purple dashed (hidden by default)
  const hasEur = dadosDiarios.some(
    (d: PerformanceDadoDiario) =>
      resolveFxAccumulatedReturn(d.ptaxEur, d.variacaoAcumuladaEur, taxaEurInicial) !== null
  );
  if (hasEur) {
    datasets.push({
      label: 'EUR Acumulado',
      data: totalSeries.map((p: PerformanceCurvePoint) => {
        const d = dailyByDate.get(getDateKey(p.data));
        return resolveFxAccumulatedReturn(d?.ptaxEur, d?.variacaoAcumuladaEur, taxaEurInicial);
      }),
      borderColor: '#6b46c1',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      borderDash: [6, 6],
      fill: false,
      yAxisID: 'y',
      hidden: true,
    });
  }

  const cotacaoUsdSeries = totalSeries.map((p: PerformanceCurvePoint) => {
    const d = dailyByDate.get(getDateKey(p.data));
    return resolveFxQuote(d?.ptaxUsd, d?.variacaoAcumuladaUsd, taxaUsdInicial);
  });

  const hasPtaxUsd = cotacaoUsdSeries.some(
    (v: number | null | undefined) => Number.isFinite(v as number) && (v as number) > 0
  );
  if (hasPtaxUsd) {
    datasets.push({
      label: 'Cotação USD',
      data: cotacaoUsdSeries,
      borderColor: '#4299e1',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      pointRadius: 0,
      spanGaps: true,
      yAxisID: 'y1',
    });
  }

  const cotacaoEurSeries = totalSeries.map((p: PerformanceCurvePoint) => {
    const d = dailyByDate.get(getDateKey(p.data));
    return resolveFxQuote(d?.ptaxEur, d?.variacaoAcumuladaEur, taxaEurInicial);
  });

  const hasPtaxEur = cotacaoEurSeries.some(
    (v: number | null | undefined) => Number.isFinite(v as number) && (v as number) > 0
  );
  if (hasPtaxEur) {
    datasets.push({
      label: 'Cotação EUR',
      data: cotacaoEurSeries,
      borderColor: '#9f7aea',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      pointRadius: 0,
      spanGaps: true,
      yAxisID: 'y2',
    });
  }

  // 7. Benchmark — CDI vindo do backend ou PRE fixo calculado no front.
  if (benchmark.tipo === 'PRE') {
    datasets.push({
      label: benchmarkLabel || benchmark.nome || 'PRE',
      data: buildPreBenchmarkData(labels, benchmark.taxaAnual ?? 0),
      borderColor: benchmark.cor || '#ed8936',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
    });
  } else if (
    dadosDiarios.length > 0 &&
    dadosDiarios.some((d: PerformanceDadoDiario) => (d.retornoAcumuladoCdi ?? 1) !== 1)
  ) {
    datasets.push({
      label: benchmarkLabel || benchmark.nome || 'CDI',
      data: totalSeries.map((p: PerformanceCurvePoint) => {
        const d = dailyByDate.get(getDateKey(p.data));
        return ((d?.retornoAcumuladoCdi ?? 1) - 1) * 100;
      }),
      borderColor: benchmark.cor || '#f56565',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
    });
  }

  // O eixo principal fica em retorno acumulado (%). Cotações USD/EUR usam eixos
  // próprios à direita para não distorcer a escala da performance.
  if (chartOptions) {
    const scales = (chartOptions['scales'] as Record<string, Record<string, unknown>>) ?? {};
    if (scales['y1']) {
      scales['y1']['display'] = hasPtaxUsd;
      scales['y1']['title'] = { display: hasPtaxUsd, text: 'Cotação USD (R$)' };
      if (hasPtaxUsd) {
        const usdValues = cotacaoUsdSeries.filter(
          (v): v is number => typeof v === 'number' && Number.isFinite(v)
        );
        const usdMin = usdValues.length ? Math.min(...usdValues) : 0;
        const usdMax = usdValues.length ? Math.max(...usdValues) : 1;
        const usdPadding = usdMax === usdMin ? 0.01 : 0;
        scales['y1']['min'] = usdMin - usdPadding;
        scales['y1']['max'] = usdMax + usdPadding;
      } else {
        delete scales['y1']['min'];
        delete scales['y1']['max'];
      }
    }
    if (scales['y2']) {
      scales['y2']['display'] = hasPtaxEur;
      scales['y2']['title'] = { display: hasPtaxEur, text: 'Cotação EUR (R$)' };
      if (hasPtaxEur) {
        const eurValues = cotacaoEurSeries.filter(
          (v): v is number => typeof v === 'number' && Number.isFinite(v)
        );
        const eurMin = eurValues.length ? Math.min(...eurValues) : 0;
        const eurMax = eurValues.length ? Math.max(...eurValues) : 1;
        const eurPadding = eurMax === eurMin ? 0.01 : 0;
        scales['y2']['min'] = eurMin - eurPadding;
        scales['y2']['max'] = eurMax + eurPadding;
      } else {
        delete scales['y2']['min'];
        delete scales['y2']['max'];
      }
    }
  }

  return { labels, datasets };
}

/**
 * Constrói o { labels, datasets } a partir de um PerformanceCurveSeries
 * (snapshot do backend — sem dadosDiarios, sem taxasUsd/Eur).
 *
 * Produz a mesma estrutura de datasets que buildChartDataFromStream,
 * mas apenas com as séries disponíveis no snapshot.
 * USD/EUR acumulado e cotações são omitidos (não estão no snapshot).
 *
 * Quando benchmarkCurve estiver presente e não-vazia, adiciona a série CDI
 * com o mesmo label/cor/eixo/transformação usados no caminho SSE (dataset 9).
 */
export function buildChartDataFromSnapshot(
  snapshotCurve: PerformanceCurveSeries,
  benchmark: BenchmarkMeta = { nome: 'CDI', cor: '#f56565' },
  benchmarkCurve?: { data: string; retornoAcumuladoCdi: number }[] | null,
  benchmarkLabel?: string
): { labels: string[]; datasets: unknown[] } | null {
  const rawTotalCurve = snapshotCurve?.total?.curve;
  if (!Array.isArray(rawTotalCurve) || rawTotalCurve.length === 0) return null;

  const rawJurosSeries = snapshotCurve?.juros ?? [];
  const rawCambioSeries = snapshotCurve?.cambio ?? [];
  const rawDerivCurve = snapshotCurve?.derivativos?.curve ?? [];
  const firstActivityIndex = findFirstDebtActivityIndex(
    rawTotalCurve,
    rawJurosSeries,
    rawCambioSeries,
    rawDerivCurve
  );
  // Mantém um ponto âncora zerado antes da primeira atividade: a Cota Total inicia em 0.
  const startIndex = Math.max(0, firstActivityIndex - 1);
  const startDate = getDateKey(rawTotalCurve[startIndex]?.data);
  const totalCurve = rawTotalCurve.slice(startIndex);

  const labels = totalCurve.map((p: { data: string }) => p.data);
  const datasets: unknown[] = [];

  // 1. Cota Total (Curva)
  datasets.push({
    label: 'Cota Total (Curva)',
    data: totalCurve.map((p: { data: string; cotaTotal?: number }) => (p.cotaTotal ?? 0) * 100),
    borderColor: '#48bb78',
    backgroundColor: 'rgba(72, 187, 120, 0.1)',
    borderWidth: 3,
    tension: 0.35,
    fill: true,
    yAxisID: 'y',
  });

  // 2. Juros (hidden by default)
  const jurosSeries = rawJurosSeries.filter(p => getDateKey(p.data) >= startDate);
  if (jurosSeries.length > 0) {
    datasets.push({
      label: 'Juros',
      data: jurosSeries.map(
        (p: { data: string; cotaJurosAcumulada?: number }) => (p.cotaJurosAcumulada ?? 0) * 100
      ),
      borderColor: '#2d3748',
      backgroundColor: 'rgba(45, 55, 72, 0.15)',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
      hidden: true,
    });
  }

  // 3. Câmbio Ponderado (hidden by default)
  // NOTE: backend DTO has typo "varacacaoAcumulada" — bind to that field
  const cambioSeries = rawCambioSeries.filter(p => getDateKey(p.data) >= startDate);
  if (cambioSeries.length > 0) {
    datasets.push({
      label: 'Câmbio Ponderado',
      data: cambioSeries.map(
        (p: { data: string; varacacaoAcumulada?: number; variacaoAcumulada?: number }) =>
          (p.varacacaoAcumulada ?? p.variacaoAcumulada ?? 0) * 100
      ),
      borderColor: '#3182ce',
      backgroundColor: 'rgba(49, 130, 206, 0.12)',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      borderDash: [8, 4],
      yAxisID: 'y',
      hidden: true,
    });
  }

  // 4. Derivativos (hidden by default)
  const derivCurve = rawDerivCurve.filter(p => getDateKey(p.data) >= startDate);
  if (
    derivCurve.length > 0 &&
    derivCurve.some(
      (p: { cotaAcumulada?: number; mtmNaCurva?: number }) =>
        (p.cotaAcumulada ?? 0) !== 0 || (p.mtmNaCurva ?? 0) !== 0
    )
  ) {
    datasets.push({
      label: 'Derivativos (Curva)',
      data: derivCurve.map((p: { cotaAcumulada?: number }) => (p.cotaAcumulada ?? 0) * 100),
      borderColor: '#9f7aea',
      backgroundColor: 'rgba(159, 122, 234, 0.15)',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
      hidden: true,
    });
  }

  // 5. Benchmark: PRE fixo no front ou CDI vindo do snapshot/backend.
  if (benchmark.tipo === 'PRE') {
    datasets.push({
      label: benchmarkLabel || benchmark.nome || 'PRE',
      data: buildPreBenchmarkData(labels, benchmark.taxaAnual ?? 0),
      borderColor: benchmark.cor || '#ed8936',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
    });
  } else if (Array.isArray(benchmarkCurve) && benchmarkCurve.length > 0) {
    const cdiByDate = new Map<string, number>();
    for (const p of benchmarkCurve) {
      cdiByDate.set(getDateKey(p.data), p.retornoAcumuladoCdi);
    }
    datasets.push({
      label: benchmarkLabel || benchmark.nome || 'CDI',
      data: labels.map((dateStr: string) => {
        const factor = cdiByDate.get(getDateKey(dateStr)) ?? 1;
        return (factor - 1) * 100;
      }),
      borderColor: benchmark.cor || '#f56565',
      backgroundColor: 'transparent',
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
    });
  }

  return { labels, datasets };
}
